import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import type { EmployeeUserResponse, PensionInsuranceEmployeeConfigModel } from '@/api/model'
import {
  usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigsPension
} from '@/api/generated/company-management-user/company-management-user'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'

interface FormValues {
  pensionInsuranceNumber: string
}

const defaultValues: FormValues = {
  pensionInsuranceNumber: '',
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  companyId: number
  employee: EmployeeUserResponse | null
  config?: PensionInsuranceEmployeeConfigModel
  onSuccess?: () => void
}

export function PensionInsuranceEmployeeDialog({ open, onOpenChange, serviceModuleId, companyId, employee, config, onSuccess }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'common'])

  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormValues>({ defaultValues })

  const { mutate, isPending } = usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigsPension()

  useEffect(() => {
    if (open) {
      reset({ pensionInsuranceNumber: config?.pensionInsuranceNumber ?? '' })
    }
  }, [open, config, reset])

  function onSubmit(values: FormValues) {
    if (!employee?.id) return

    const payload = {
      id: config?.id ?? 0,
      employeeUserId: employee.id,
      companyId,
      serviceModuleId,
      pensionInsuranceNumber: values.pensionInsuranceNumber || '',
    }

    mutate(
      { serviceModuleId, data: payload },
      {
        onSuccess: () => {
          notify.success(t('insurance.dialog.saveSuccess'))
          onSuccess?.()
          onOpenChange(false)
        },
        onError: (error) => {
          notify.apiError(error, t('insurance.dialog.saveError'))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('insurance.dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>{t('columns.firstName', { ns: 'common' })}</Label>
            <Input value={employee?.firstName ?? '—'} disabled />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('columns.lastName', { ns: 'common' })}</Label>
            <Input value={employee?.lastName ?? '—'} disabled />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="insurance-number">{t('insurance.dialog.insuranceNumber')}</Label>
            <Input
              id="insurance-number"
              {...register('pensionInsuranceNumber')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending || isSubmitting}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending || isSubmitting}>
            {(isPending || isSubmitting) && <Loader2 className="size-4 animate-spin" />}
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
